// TES-1092: Administrative Übersicht für global angemeldete super_admins.
// Owner-Entscheid (2026-06-11): super_admin ist NICHT operativ — statt der
// QM-Kacheln (Prüfungen/Mängel) bekommt das '/'-Dashboard administrative
// Einstiegskacheln (Benutzer/Rollen/Teams/Systemzustand). Kleinste sinnvolle
// Lösung: Link-Kacheln auf bestehende Admin-Seiten, Labels aus nav.* (i18n).
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronRight, Users, Shield, Server, Activity, Bell, UsersRound } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PageContainer } from "@/components/layout/PageContainer";
import { useAuth } from "@/hooks/useAuth";
import { getUserGreetingName } from "@/lib/profileUtils";

interface AdminTile {
  nameKey: string;
  href: string;
  icon: typeof Users;
  testId: string;
}

// Kacheln spiegeln die Super-Admin-/Admin-Navigation (navConfig.ts) — keine neuen Ziele.
const ADMIN_TILES: AdminTile[] = [
  {
    nameKey: "nav.allUsers",
    href: "/admin/all-users",
    icon: Users,
    testId: "admin-tile-all-users",
  },
  { nameKey: "nav.roles", href: "/admin/roles", icon: Shield, testId: "admin-tile-roles" },
  { nameKey: "nav.teams", href: "/admin/teams", icon: UsersRound, testId: "admin-tile-teams" },
  {
    nameKey: "nav.edgeFunctions",
    href: "/admin/edge-functions",
    icon: Server,
    testId: "admin-tile-edge-functions",
  },
  {
    nameKey: "nav.setupStatus",
    href: "/admin/setup-status",
    icon: Activity,
    testId: "admin-tile-setup-status",
  },
  {
    nameKey: "nav.notificationOverview",
    href: "/admin/notification-overview",
    icon: Bell,
    testId: "admin-tile-notifications",
  },
];

function AdminTileCard({ tile, label }: { tile: AdminTile; label: string }) {
  const Icon = tile.icon;
  return (
    <Link to={tile.href} data-testid={tile.testId} className="group block">
      <Card className="h-full transition-colors hover:border-primary/40 hover:bg-surface-container-low">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <span className="flex-1 min-w-0 truncate font-medium">{label}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function SuperAdminDashboard() {
  const { t } = useTranslation();
  const { profile, user } = useAuth();

  const userName = getUserGreetingName(profile, user?.email, t("common.user"));

  return (
    <PageContainer className="space-y-8" data-testid="super-admin-dashboard">
      {/* Header — gleiche Anatomie wie das operative Dashboard (Breadcrumb + Titel) */}
      <div>
        <div className="flex items-center gap-2 text-muted-foreground text-label-caps mb-2">
          <span>{t("dashboard.bento.breadcrumbWorkspace")}</span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-primary font-bold">{userName}</span>
        </div>
        <h1 className="font-display text-[2rem] font-bold leading-[1.2] text-primary">
          {t("dashboard.adminOverview")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("nav.administration")}</p>
      </div>

      <div className="grid gap-4 container-grid-3">
        {ADMIN_TILES.map((tile) => (
          <AdminTileCard key={tile.testId} tile={tile} label={t(tile.nameKey)} />
        ))}
      </div>
    </PageContainer>
  );
}
